import React, {
    createContext,
    useContext,
    useState,
    useEffect,
    useCallback,
    useRef,
    useMemo,
} from 'react';
import io from 'socket.io-client';
import { detectIncognito } from 'detectincognitojs';
import useHttp from '../hooks/useHttp';
import { getAthorisationSearchParams } from '../utils/getAthorisationSearchParams';
import { currentLang } from '../App';
import { useToken } from '../hooks/useToken';

const Context = createContext();
export const useChat = () => useContext(Context);

export const Provider = ({ children }) => {
    const widgetId = localStorage.getItem('widgetId');
    const authParams = getAthorisationSearchParams();

    const { token, setToken, updateToken } = useToken();

    const [socketInstance, setSocketInstance] = useState(null);
    const [isTokenChanged, setIsTokenChanged] = useState(false);
    const [isWidgetBlocked, setIsWidgetBlocked] = useState(false);
    const [isIncognito, setIsIncognito] = useState(null);
    const [isDialogActive, setDialogActive] = useState(false);
    const [dialogId, setDialogId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [showWidget, setShowWidget] = useState(true);
    const [refreshBite, setRefreshBite] = useState(0);

    const [authWidgetData, fetchAuthWidgetData, authError, , ,dialogStatus] = useHttp(`widget/${widgetId}?${authParams}`);
    const [staticWidgetData, fetchStaticWidgetData, staticError] = useHttp(`widget/${widgetId}`);

    const isInitializedRef = useRef(false);

    const fetchAuthorisationData = useCallback(async () => {
        try {
            await fetchAuthWidgetData({
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            });
        } catch (err) {
            console.error('Authorization error:', err);
        }
    }, [fetchAuthWidgetData, token]);

    const initializeSocket = useCallback(() => {
        if (socketInstance) {
            socketInstance.off('visitor.status');
            socketInstance.disconnect();
            setSocketInstance(null);
        }

        const localToken = localStorage.getItem('token');
        if (!localToken) return;

        const newSocket = io(process.env.REACT_APP_SOCKET_URL, {
            query: { token: localToken },
            reconnection: true,
            reconnectionDelay: 500,
        });

        newSocket.on('visitor.status', (data) => {
            setIsWidgetBlocked(data.status === 'blocked');
            if (data.status !== 'blocked') {
                fetchAuthorisationData();
            }
        });

        setSocketInstance(newSocket);
    }, [fetchAuthorisationData]);

    useEffect(() => {
        const detectMode = async () => {
            const result = await detectIncognito();
            const isPrivate = Number(result.isPrivate);
            localStorage.setItem('isIncognito', isPrivate);
            setIsIncognito(isPrivate);
        };
        detectMode();
    }, []);

    useEffect(() => {
        if (isIncognito !== null && !isWidgetBlocked && !isInitializedRef.current) {
            isInitializedRef.current = true;
            fetchAuthorisationData();
        }
    }, [isIncognito, isWidgetBlocked, fetchAuthorisationData]);

    useEffect(() => {
        if (authError) setIsWidgetBlocked(true);
    }, [authError]);

    useEffect(() => {
        const newToken = authWidgetData?.data?.token;
        if (newToken && newToken !== token) {
            const { expires, refresh, refresh_expires, id: sc_visitor } = authWidgetData.data;
            localStorage.setItem('token', newToken);
            localStorage.setItem('expires', expires);
            localStorage.setItem('refresh', refresh);
            localStorage.setItem('refresh_expires', refresh_expires);
            localStorage.setItem('sc_visitor', sc_visitor);
            setToken(newToken);
        }
    }, [authWidgetData?.data?.token]);

    useEffect(() => {
        if (!token) return;
        initializeSocket();

        return () => {
            if (socketInstance) {
                socketInstance.off('visitor.status');
                socketInstance.disconnect();
                setSocketInstance(null);
            }
        };
    }, [token]);

    useEffect(() => {
        const checkTokenExpiration = () => {
            const expires = localStorage.getItem('expires');
            if (!expires) return;

            const expirationDate = new Date(expires);
            const now = new Date();
            const minutesLeft = (expirationDate - now) / 1000 / 60;

            if (minutesLeft <= 2) updateToken();
        };

        const intervalId = setInterval(checkTokenExpiration, 180000);
        return () => clearInterval(intervalId);
    }, [updateToken]);

    useEffect(() => {
        if (authWidgetData && localStorage.getItem('dialogStatus') === 'active' && dialogStatus === 'active') {
            setDialogActive(true);
        }
    }, [authWidgetData, dialogStatus]);

    const appLang = useMemo(() => {
        return staticWidgetData?.data.languages?.find(lang => lang === currentLang) || staticWidgetData?.default_language;
    }, [staticWidgetData]);

    const value = {
        token,
        setToken,
        widgetId,
        socketInstance,
        setSocketInstance,
        isTokenChanged,
        setIsTokenChanged,
        isWidgetBlocked,
        setIsWidgetBlocked,
        initializeSocket,
        dialogId,
        setDialogId,
        messages,
        setMessages,
        isDialogActive,
        setDialogActive,
        widgetData: staticWidgetData?.data,
        name: staticWidgetData?.data?.name,
        logotype: staticWidgetData?.data?.logotype,
        copyDisabled: staticWidgetData?.data?.hide_copy,
        titleMessages: staticWidgetData?.data?.default_messages,
        visitorMessages: staticWidgetData?.data?.visitor_messages,
        fetchStaticWidgetData,
        showWidget,
        setShowWidget,
        refreshBite,
        setRefreshBite,
        defaultLang: appLang,
    };

    return <Context.Provider value={value}>{children}</Context.Provider>;
};
